import { useGraphQLMutation } from '@opengov/bnp-core';
import { useQueryClient } from '@tanstack/react-query';
import { IPublicationTocSettings } from '@opengov/stories-shared/types';

import { graphql } from '../../../../../gql-generated/hasura';
import {
  UpdatePublicationTocSettingsForOnlinePublicationMutation,
  UpdatePublicationTocSettingsForOnlinePublicationMutationVariables
} from '../../../../../gql-generated/hasura/graphql';
import { HASURA_SERVICE_URL } from '../../../../../config/config';
import { useToast } from '../../../Toast/context/useToast.context';

import { setupOnlinePublicationQueryKeys } from './setupOnlinePublicationQueryKeys';

const UpdatePublicationTocSettingsForOnlinePublicationDocument = graphql(`
  mutation UpdatePublicationTocSettingsForOnlinePublication(
    $id: uuid!
    $set: publicationTOCSettings_set_input!
  ) {
    update_publicationTOCSettings_by_pk(pk_columns: { id: $id }, _set: $set) {
      id
      columnLayoutNumber
      createdBy
      headerHeightSize
      headerImageUrl
      menuTitle
      pageTitle
      showTableOfContents
      createdAt
      publicationId
      updatedAt
      description
      hasUserToggledToc
    }
  }
`);

export type UseSaveTocSettingsMessages = {
  errorTitle: string;
  errorBody?: string;
};

export type UseSaveTocSettingsContext = {
  publicationId: string;
};

export function useSaveTocSettings(
  context: UseSaveTocSettingsContext,
  messages: UseSaveTocSettingsMessages
) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useGraphQLMutation<
    UpdatePublicationTocSettingsForOnlinePublicationMutation,
    UpdatePublicationTocSettingsForOnlinePublicationMutationVariables
  >({
    mutationKey: [...setupOnlinePublicationQueryKeys.all, 'saveTocSettings', context],
    document: UpdatePublicationTocSettingsForOnlinePublicationDocument,
    url: HASURA_SERVICE_URL,
    options: {
      onSuccess: (data) => {
        const updated = data?.update_publicationTOCSettings_by_pk;
        if (updated) {
          queryClient.setQueryData(
            setupOnlinePublicationQueryKeys.tocSettings({ publicationId: context.publicationId }),
            (old: { publicationTOCSettings: IPublicationTocSettings[] } | undefined) => {
              const [first, ...rest] = old?.publicationTOCSettings ?? [];
              return {
                publicationTOCSettings: [
                  { ...(first ?? {}), ...(updated as unknown as IPublicationTocSettings) },
                  ...rest
                ]
              };
            }
          );
        }
      },
      onError: () =>
        toggleToast({
          title: messages.errorTitle,
          body: messages.errorBody,
          severity: 'error'
        })
    }
  });
}
